/**
 * PRD.253 — Sync Health helpers.
 *
 * Pure (non-React) helpers for deriving sync-health state, capturing local
 * upload errors, and recording retry attempts. All persistence is in
 * localStorage so a support bundle can be built even after a reload.
 *
 * NOTE: stack traces are captured ONLY for the support bundle — never render
 * them to clinic users (see PRD §8).
 */

// ─── Derived sync state ───

export type SyncHealthState =
  | 'connected'
  | 'syncing'
  | 'offline'
  | 'stale'
  | 'stuck'
  | 'unknown';

// ─── Thresholds (PRD §7.3) ───

export const SYNC_STALE_MINUTES = 15;
export const SYNC_STUCK_MINUTES = 30;
export const MAX_RECENT_UPLOAD_ERRORS = 20;
export const MAX_RETRY_LOGS = 20;

// ─── localStorage keys ───

const UPLOAD_ERRORS_KEY = 'powercare_upload_errors';
const RETRY_LOGS_KEY = 'powercare_retry_logs';

// ─── Captured-error / retry shapes ───

export interface LocalUploadErrorEntry {
  timestamp: string;
  writeUrl?: string;
  operationCount?: number;
  error: {
    message: string;
    name?: string;
    stack?: string;
  };
}

export interface SyncRetryLogEntry {
  timestamp: string;
  /** What kind of recovery action was attempted. */
  action: 'retry' | 'force-resync';
  pendingBefore?: number;
  pendingAfter?: number;
  outcome: 'started' | 'success' | 'error';
  detail?: string;
}

// ─── Input snapshot for deriving state ───

export interface SyncStateInput {
  /** PowerSync reports a live connection to the sync service. */
  connected: boolean;
  /** Browser navigator.onLine — the device has network at all. */
  online: boolean;
  /** Currently downloading or uploading. */
  inFlight: boolean;
  /** Number of CRUD ops waiting to upload. */
  pendingCount: number;
  /** ISO timestamp of the oldest pending op, if known. */
  oldestPendingAt?: string | null;
  /** ISO/locale string of the last successful sync, if known. */
  lastSyncedAt?: string | null;
  /** Count of recent captured upload errors. */
  recentErrorCount: number;
  /** Did the most recent retry fail to drain the queue? */
  retryDidNotDrain?: boolean;
  /**
   * An active sync error reported by PowerSync (download/websocket OR upload).
   * Captures the "can't reach the sync service at all" case (e.g. bad sync URL,
   * blocked endpoint) where no upload error is ever recorded because no
   * connection was established.
   */
  hasSyncError?: boolean;
}

// ─── localStorage helpers ───

function readJsonArray<T>(key: string): T[] {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as T[]) : [];
  } catch {
    return [];
  }
}

function writeJsonArray<T>(key: string, value: T[]): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Best-effort — storage may be full or disabled.
  }
}

/**
 * Capture a single upload failure locally. Keeps only the most recent
 * MAX_RECENT_UPLOAD_ERRORS entries (newest first).
 */
export function recordUploadError(entry: LocalUploadErrorEntry): void {
  const next = [entry, ...getRecentUploadErrors()].slice(0, MAX_RECENT_UPLOAD_ERRORS);
  writeJsonArray(UPLOAD_ERRORS_KEY, next);
}

/** Read recent upload errors (newest first). */
export function getRecentUploadErrors(): LocalUploadErrorEntry[] {
  return readJsonArray<LocalUploadErrorEntry>(UPLOAD_ERRORS_KEY);
}

/** Clear captured upload errors (e.g. after a successful drain). */
export function clearUploadErrors(): void {
  try {
    localStorage.removeItem(UPLOAD_ERRORS_KEY);
  } catch {
    // ignore
  }
}

/** Append a retry/force-resync log entry (newest first, capped). */
export function recordRetryLog(entry: SyncRetryLogEntry): void {
  const next = [entry, ...getRetryLogs()].slice(0, MAX_RETRY_LOGS);
  writeJsonArray(RETRY_LOGS_KEY, next);
}

/** Read retry logs (newest first). */
export function getRetryLogs(): SyncRetryLogEntry[] {
  return readJsonArray<SyncRetryLogEntry>(RETRY_LOGS_KEY);
}

// ─── Derivation ───

function minutesSince(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  return (Date.now() - then) / 60_000;
}

/**
 * Derive a single sync-health state from a snapshot.
 *
 * Priority order (PRD §7.1 / §7.3):
 *  - offline   → no network
 *  - stuck     → online, pending ops + (errors / didn't drain / oldest > stuck threshold)
 *  - stale     → online, pending ops sitting past the stale threshold
 *  - syncing   → actively transferring
 *  - connected → live link, queue empty (or draining cleanly)
 *  - unknown   → not enough signal
 */
export function deriveSyncHealthState(input: SyncStateInput): SyncHealthState {
  if (!input.online) return 'offline';

  const oldestMin = minutesSince(input.oldestPendingAt);
  const hasPending = input.pendingCount > 0;

  if (hasPending) {
    const stuckByAge = oldestMin != null && oldestMin >= SYNC_STUCK_MINUTES;
    const stuckBySignal =
      input.recentErrorCount > 0 ||
      input.retryDidNotDrain === true ||
      input.hasSyncError === true;
    // Online + a non-draining queue + an active failure signal = stuck. We do
    // NOT require a live PowerSync connection: a device that can't connect at
    // all (bad sync URL, blocked endpoint) while holding pending writes is the
    // most stuck of all.
    if (stuckByAge || stuckBySignal) {
      return 'stuck';
    }
    const staleByAge = oldestMin != null && oldestMin >= SYNC_STALE_MINUTES;
    if (staleByAge) return 'stale';
  }

  if (input.inFlight) return 'syncing';
  if (input.connected) return 'connected';
  return 'unknown';
}

/**
 * True when a sync error is an expired-auth/JWT failure — the session token
 * lapsed so the device stopped receiving updates and silently shows stale data.
 * PowerSync surfaces this as code `PSYNC_S2103` ("JWT has expired"); we also
 * match looser token-expiry phrasings defensively.
 */
export function isAuthExpiredSyncError(message: string | null | undefined): boolean {
  if (!message) return false;
  return /PSYNC_S2103|jwt[^a-z]*(has )?expired|token[^a-z]*(has )?expired|expired token/i.test(
    message,
  );
}

/**
 * True when sync is caught in a re-open loop. After a sync-rules change the
 * server bumps the checkpoint version; a client whose local state can't
 * reconcile tears down and re-opens the stream repeatedly — surfacing as
 * `ConnectionClosedError: Connection is busy re-opening` (and aborted
 * in-flight checkpoint fetches). It never settles, so the page silently shows
 * stale data. Recovery is a local clear + re-sync (Force Resync).
 */
export function isReopenLoopError(message: string | null | undefined): boolean {
  if (!message) return false;
  return /busy re-?opening|ConnectionClosedError/i.test(message);
}

/** How long the re-open loop must persist before we treat it as genuinely stuck. */
export const SYNC_REOPEN_STUCK_MS = 25_000;

/** Human-readable label + tone for a sync-health state. */
export function describeSyncHealthState(state: SyncHealthState): {
  label: string;
  tone: 'ok' | 'warn' | 'error' | 'neutral';
} {
  switch (state) {
    case 'connected':
      return { label: 'Connected', tone: 'ok' };
    case 'syncing':
      return { label: 'Syncing', tone: 'neutral' };
    case 'offline':
      return { label: 'Offline', tone: 'warn' };
    case 'stale':
      return { label: 'Stale', tone: 'warn' };
    case 'stuck':
      return { label: 'Stuck', tone: 'error' };
    default:
      return { label: 'Unknown', tone: 'neutral' };
  }
}
